import type { ReactNode } from 'react';
import { Text } from '@belong/ui';
import type {
  ActionableResolutionOption,
  FailedPaymentsAudience,
  ResolutionOption,
} from '../failed-payments-audience';
import type { ConsumerFailedPaymentModel } from '../failed-payments.types';
import { clsx } from 'clsx';
import { ResolveModalFooter } from '../resolve-modal-footer';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import { FailedPaymentBalanceSummary } from '../failed-payment-balance-summary';

type OptionsStepProps = {
  audience: FailedPaymentsAudience;
  failedPayments: ConsumerFailedPaymentModel[];
  options?: ResolutionOption[];
  subtitle?: string | null;
  optionSubtitles?: Partial<Record<ResolutionOption, string | null>>;
  selectedOption: ActionableResolutionOption | null;
  onSelectOption: (option: ActionableResolutionOption) => void;
  onSubmit: () => void;
  submitLabel?: ReactNode;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  wideSubmit?: boolean;
  footerClassName?: string;
  pinnedSubmitWidth?: boolean;
  subsection?: ReactNode;
};

export function OptionsStep({
  audience,
  failedPayments,
  options,
  subtitle,
  optionSubtitles,
  selectedOption,
  onSelectOption,
  onSubmit,
  submitLabel,
  submitDisabled,
  submitLoading,
  wideSubmit,
  footerClassName,
  pinnedSubmitWidth,
  subsection,
}: OptionsStepProps) {
  const primaryFailedPayment = failedPayments[0];
  const isHomeowner = audience.key === 'homeowner';
  const visibleOptions = options ?? audience.options;
  const useCompactGrid = visibleOptions.length <= 4;

  if (!primaryFailedPayment) return null;

  return (
    <div className="flex flex-col gap-2xl">
      <div
        className={clsx('flex flex-col', isHomeowner ? 'gap-xl' : 'gap-2xl')}
      >
        <div className="flex flex-col gap-xs">
          <Text
            variant="h2"
            fontWeight="semibold"
            className="whitespace-pre-line px-2sm text-center md:px-md"
          >
            {FAILED_PAYMENTS_STRINGS['modal.options.title']}
          </Text>
          {subtitle ? (
            <Text fontWeight="semibold" className="text-center text-navy">
              {subtitle}
            </Text>
          ) : null}
        </div>

        <FailedPaymentBalanceSummary failedPayments={failedPayments} />
      </div>

      <div className={clsx('flex flex-col', isHomeowner ? 'gap-sm' : 'gap-md')}>
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['modal.options.prompt']}
        </Text>
        <div
          className={clsx(
            'grid grid-cols-1',
            isHomeowner ? 'gap-sm' : 'gap-md',
            useCompactGrid ? 'md:grid-cols-2' : 'md:grid-cols-3',
          )}
        >
          {visibleOptions.map((option) => {
            const isSelected = selectedOption === option;
            const optionSubtitle = optionSubtitles?.[option];

            return (
              <button
                key={option}
                type="button"
                onClick={() => onSelectOption(option)}
                className={clsx(
                  'flex cursor-pointer items-center justify-center rounded border-0 px-md text-center shadow transition-colors',
                  !useCompactGrid && isHomeowner && 'md:px-[7px]',
                  useCompactGrid
                    ? 'h-[64px]'
                    : isHomeowner
                      ? 'h-[64px] md:h-[110px]'
                      : 'h-[80px] md:h-[110px]',
                  isSelected ? 'bg-navy' : 'bg-white',
                )}
              >
                <span className="flex flex-col">
                  <Text
                    variant="body"
                    fontWeight={isSelected ? 'semibold' : 'regular'}
                    className={clsx(
                      'whitespace-normal md:whitespace-pre-line',
                      isSelected ? 'text-white' : 'text-navy',
                    )}
                  >
                    {FAILED_PAYMENTS_STRINGS[audience.optionLabelKeys[option]]}
                  </Text>
                  {optionSubtitle ? (
                    <Text
                      variant="p1"
                      fontWeight={isSelected ? 'semibold' : 'regular'}
                      className={isSelected ? 'text-white' : 'text-navy'}
                    >
                      {optionSubtitle}
                    </Text>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {subsection}

      <ResolveModalFooter
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={submitDisabled ?? !selectedOption}
        loading={submitLoading}
        wideSubmit={wideSubmit}
        className={footerClassName}
        pinnedSubmitWidth={isHomeowner || pinnedSubmitWidth}
      />
    </div>
  );
}
